import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Shield, Building2, Home, ChevronDown, Rocket, Award, Brain } from 'lucide-react';

const Hero = () => {
  const { t, language } = useLanguage();

  // Logo URLs
  const logoTR = 'https://customer-assets.emergentagent.com/job_94139453-6f7a-4399-aef4-e5acc06243f4/artifacts/xea16yuo_ChatGPT%20Image%208%20Oca%202026%2013_29_22.png';
  const logoEN = 'https://customer-assets.emergentagent.com/job_94139453-6f7a-4399-aef4-e5acc06243f4/artifacts/u7xdry6v_ChatGPT%20Image%208%20Oca%202026%2013_30_27.png';

  const heroContent = {
    en: {
      headline: 'We Protect Your Business, Your Work, Your Value.',
      subheadline: 'Expert legal counsel in Intellectual Property, Corporate Law, and Real Estate across Turkey',
      cta: 'Schedule a Consultation',
      secondary: 'Our Practice Areas',
      services: [
        { icon: Shield, name: 'Intellectual Property' },
        { icon: Award, name: 'Trademark Law' },
        { icon: Building2, name: 'Corporate Law' },
        { icon: Home, name: 'Real Estate' },
        { icon: Rocket, name: 'Start-up Law' },
        { icon: Brain, name: 'AI Law' },
      ],
      scrollText: 'Learn More'
    },
    tr: {
      headline: 'İşinizi, Eserinizi, Değerinizi Koruyoruz.',
      subheadline: 'Fikri Mülkiyet, Ticaret Hukuku ve Gayrimenkul alanlarında uzman hukuki danışmanlık',
      cta: 'Randevu Talep Edin',
      secondary: 'Faaliyet Alanlarımız',
      services: [
        { icon: Shield, name: 'Fikri Mülkiyet' },
        { icon: Award, name: 'Marka Hukuku' },
        { icon: Building2, name: 'Ticaret Hukuku' },
        { icon: Home, name: 'Gayrimenkul' },
        { icon: Rocket, name: 'Start-Up Hukuku' },
        { icon: Brain, name: 'Yapay Zeka Hukuku' },
      ],
      scrollText: 'Daha Fazla'
    },
    es: {
      headline: 'Protegemos Su Negocio, Su Obra, Su Valor.',
      subheadline: 'Asesoría legal especializada en Propiedad Intelectual, Derecho Mercantil e Inmobiliario en Turquía',
      cta: 'Agendar Consulta',
      secondary: 'Áreas de Práctica',
      services: [
        { icon: Shield, name: 'Propiedad Intelectual' },
        { icon: Award, name: 'Derecho de Marcas' },
        { icon: Building2, name: 'Derecho Mercantil' },
        { icon: Home, name: 'Derecho Inmobiliario' },
        { icon: Rocket, name: 'Derecho de Start-ups' },
        { icon: Brain, name: 'Derecho de IA' },
      ],
      scrollText: 'Más Información'
    },
    fr: {
      headline: 'Nous Protégeons Votre Entreprise, Votre Œuvre, Votre Valeur.',
      subheadline: 'Conseil juridique expert en Propriété Intellectuelle, Droit des Affaires et Immobilier en Turquie',
      cta: 'Prendre Rendez-vous',
      secondary: 'Nos Domaines',
      services: [
        { icon: Shield, name: 'Propriété Intellectuelle' },
        { icon: Award, name: 'Droit des Marques' },
        { icon: Building2, name: 'Droit des Affaires' },
        { icon: Home, name: 'Droit Immobilier' },
        { icon: Rocket, name: 'Droit des Start-ups' },
        { icon: Brain, name: 'Droit de l\'IA' },
      ],
      scrollText: 'En Savoir Plus'
    },
  };

  const content = heroContent[language] || heroContent.en;
  const currentLogo = language === 'tr' ? logoTR : logoEN;

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen bg-[#0f1f35] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#152a45]" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl" />
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 min-h-screen flex flex-col">
        <div className="flex-1 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Tagline */}
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-px bg-blue-400" />
              <span className="text-blue-300 font-medium tracking-wide uppercase text-sm">
                {content.tagline}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              {content.headline}
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-blue-200 leading-relaxed max-w-xl">
              {content.subheadline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="group flex items-center justify-center gap-3 bg-white text-[#0f1f35] px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
              >
                {content.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="group flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-xl text-lg font-medium hover:bg-white/20 transition-all"
              >
                {content.secondary}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Service Pills */}
            <div className="flex flex-wrap gap-3 pt-6">
              {content.services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-3 hover:bg-white/20 transition-colors cursor-pointer"
                    onClick={() => scrollToSection('services')}
                  >
                    <IconComponent className="w-5 h-5 text-blue-300" />
                    <div>
                      <span className="text-white font-medium text-sm">{service.name}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right - Logo Display */}
          <div className="hidden lg:flex justify-center items-center">
            <div className="relative">
              {/* Main Logo Card */}
              <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-12 border border-white/20 shadow-2xl">
                <img 
                  src={currentLogo} 
                  alt={language === 'tr' ? 'HH Hukuk Bürosu' : 'HH Law Office'}
                  className="w-80 h-auto object-contain rounded-2xl logo-inverted"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="flex justify-center pt-8">
          <button
            onClick={() => scrollToSection('services')}
            className="flex flex-col items-center gap-2 text-blue-300 hover:text-white transition-colors group"
          >
            <span className="text-sm font-medium">{content.scrollText}</span>
            <ChevronDown className="w-6 h-6 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
